import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CryptoJS from 'crypto-js';
import { jwtDecode } from "jwt-decode";
import amns_logo_tr from '../../../../App/Assets/amns_logo_tr.png';
import { Form, Input, Button, Card, message, Modal } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined } from '@ant-design/icons';
import { ENDPOINTS, MESSAGES, REQUEST_TYPES } from '../helpers/apiConfig';
import apiClient from '../helpers/apiHelper';

export const LoginPage = ({ setToken }) => {
    const [loginLoading, setLoginLoading] = useState(false);
    const [sendEmailLoading, setSendEmailLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [forceResetVisible, setForceResetVisible] = useState(false);
    const [tempEmail, setTempEmail] = useState('');
    const [resetLoading, setResetLoading] = useState(false);
    const navigate = useNavigate();

    const buildSecretKey = (username) => {
        const now = new Date();
        const year = now.getFullYear();
        const date = String(now.getDate()).padStart(2, '0');
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const pepper = `${year}${date}${month}`;
        return (username + pepper).split('').reverse().join('');
    };

    const encryptPassword = (password, secretKey) => {
        return CryptoJS.AES.encrypt(password, secretKey).toString();
    };

    const onFinish = async (values) => {
        setLoginLoading(true);
        const { email, password } = values;

        const lowerEmail = email.toLowerCase().trim();
        const secretKey = buildSecretKey(lowerEmail);
        const encryptedPassword = encryptPassword(password, secretKey);

        try {
            const response = await apiClient(ENDPOINTS.AUTH.LOGIN, {
                method: 'POST',
                body: JSON.stringify({
                    email: lowerEmail,
                    password: encryptedPassword,
                    request_type: REQUEST_TYPES.LOGIN
                })
            });

            const { token, user } = response || {};

            if (user?.must_change_password) {
                setTempEmail(lowerEmail);
                setForceResetVisible(true);
            } else if (token) {
                sessionStorage.setItem('token', token);
                setToken(token);
                message.success(MESSAGES.SUCCESS.LOGIN);
                const decoded = jwtDecode(token);
                navigate("/logsheet/dashboard");
            }
        } catch (error) {
            message.error(error?.message);
        } finally {
            setLoginLoading(false);
        }
    }

    const handleResetSubmit = async (values) => {
        setSendEmailLoading(true);
        try {
            if (values.email?.endsWith('amns.in')) {
                message.error("Please use your company password");
            } else {
                const response = await apiClient(ENDPOINTS.AUTH.REGISTER, {
                    method: 'POST',
                    body: JSON.stringify({
                        request_type: REQUEST_TYPES.FORGOT_PASSWORD_MAIL,
                        email: values.email ? values.email.toLowerCase().trim() : "",
                    })
                });

                if (response.success) {
                    message.success(MESSAGES.SUCCESS.FORGOT_PASSWORD_MAIL);
                    setIsModalOpen(false);
                }
            }
        } catch (err) {
            message.error(MESSAGES.ERROR.FORGOT_PASSWORD_MAIL);
        } finally {
            setSendEmailLoading(false);
        }
    };

    const [loginForm] = Form.useForm();
    return (
        <div>
            <img
                src={amns_logo_tr}
                alt="amns_logo"
                style={{
                    position: "absolute",
                    top: "10px",
                    left: "10px",
                    height: "50px"
                }}
            />

            <div className='login-bg flex justify-center items-center bg-[#DEDEDE]'>
                <Card className='w-full max-w-md shadow-2xl'>
                    <div className='text-center mb-8'>
                        <h1 className='text-3xl font-bold mb-2'>Digital Logsheet</h1>
                        <p className='text-md'>Sign in to your account</p>
                    </div>
                    <Form form={loginForm} name="login" onFinish={onFinish} layout='vertical' size="large">
                        <Form.Item name="email" rules={[{ required: true, message: 'Please enter your email!' }]}>
                            <Input prefix={<UserOutlined />} placeholder="Email" />
                        </Form.Item>
                        <Form.Item name="password" rules={[{ required: true, message: 'Please enter your password!' }]}>
                            <Input.Password prefix={<LockOutlined />} placeholder="Password" />
                        </Form.Item>

                        <div className="text-right">
                            <Button type="link" onClick={() => setIsModalOpen(true)} className="p-0 danger" danger>
                                Forgot Password?
                            </Button>
                        </div>

                        <Form.Item>
                            <Button type="primary" htmlType="submit" loading={loginLoading} block danger>
                                Sign In
                            </Button>
                        </Form.Item>
                    </Form>
                </Card>

                <Modal
                    title="Forgot Password"
                    open={isModalOpen}
                    onCancel={() => setIsModalOpen(false)}
                    footer={null}
                    centered
                    destroyOnClose
                >
                    <Form layout="vertical" onFinish={handleResetSubmit}>
                        <p>Enter your registered email. If found, we will send your password to your inbox.</p>

                        <Form.Item
                            name="email"
                            rules={[
                                { required: true, message: 'Please input your email!' },
                                { type: 'email', message: 'Please enter a valid email!' }
                            ]}
                        >
                            <Input prefix={<MailOutlined />} placeholder="Email Address" />
                        </Form.Item>

                        <Form.Item>
                            <Button type="primary" htmlType="submit" loading={sendEmailLoading} block danger>
                                Send Password
                            </Button>
                        </Form.Item>
                    </Form>
                </Modal>

                <Modal
                    title={<span style={{ color: '#E5322C' }}>Secure Your Account</span>}
                    open={forceResetVisible}
                    footer={null}
                    closable={false}
                    maskClosable={false}
                    keyboard={false}
                    centered
                >
                    <div className="mb-4">
                        <p>Welcome! For your security, you must update your <b>temporary password</b> before accessing
                            the
                            portal.</p>
                    </div>

                    <Form
                        layout="vertical"
                        onFinish={async (values) => {
                            setResetLoading(true);
                            try {
                                await apiClient(ENDPOINTS.AUTH.UPDATE_PASSWORD, {
                                    method: 'POST',
                                    body: JSON.stringify({
                                        request_type: REQUEST_TYPES.UPDATE_FIRST_PW,
                                        email: tempEmail,
                                        new_password: values.newPassword
                                    })
                                });
                                message.success(MESSAGES.SUCCESS.UPDATE_FIRST_PW);
                                setForceResetVisible(false);
                                window.location.reload();
                            } catch (err) {
                                message.error(MESSAGES.ERROR.UPDATE_FIRST_PW);
                            } finally {
                                setResetLoading(false);
                            }
                        }}
                    >
                        <Form.Item
                            label="New Password"
                            name="newPassword"
                            rules={[
                                {
                                    required: true,
                                    message: 'Please enter your new password'
                                },
                                {
                                    min: 8,
                                    message: 'Password must be at least 8 characters long'
                                },
                                {
                                    pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
                                    message: 'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'
                                }
                            ]}
                        >
                            <Input.Password prefix={<LockOutlined />} placeholder="Enter new password" />
                        </Form.Item>

                        <Form.Item
                            label="Confirm New Password"
                            name="confirm"
                            dependencies={['newPassword']}
                            rules={[
                                { required: true, message: 'Please confirm your password' },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (!value || getFieldValue('newPassword') === value) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error('Passwords do not match!'));
                                    },
                                }),
                            ]}
                        >
                            <Input.Password prefix={<LockOutlined />} placeholder="Repeat new password" />
                        </Form.Item>

                        <Form.Item>
                            <Button type="primary" htmlType="submit" loading={resetLoading} block danger size="large">
                                Update Password & Continue
                            </Button>
                        </Form.Item>
                    </Form>
                </Modal>
            </div>
        </div>
    );

}
